import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Button, Alert, CircularProgress, Card, CardContent
} from '@mui/material';
import {
  Facebook as FacebookIcon,
  Warning as WarningIcon,
  ArrowBack as BackIcon
} from '@mui/icons-material';
import api from '../../config/api';

// Shown when a Facebook re-authentication reduced page scope. The new credential
// is held server-side until the user explicitly confirms (or cancels and re-OAuths
// selecting all pages). Prevents the silent BigFace-style token revocation.
const FacebookReauthConfirm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tempId = searchParams.get('temp_id');

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [warning, setWarning] = useState(null);

  useEffect(() => {
    if (!tempId) {
      setError('Invalid re-authentication session.');
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const resp = await api.get('/api/connectors/facebook/reauth/pending', { params: { temp_id: tempId } });
        if (resp.data.success) {
          setWarning(resp.data.scope_warning);
        } else {
          setError(resp.data.message || 'Could not load re-authentication session.');
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Could not load re-authentication session.');
      } finally {
        setLoading(false);
      }
    })();
  }, [tempId]);

  const handleProceed = async () => {
    setSubmitting(true);
    setError('');
    try {
      const resp = await api.post('/api/connectors/facebook/reauth/confirm', { temp_id: tempId });
      if (!resp.data.success) throw new Error(resp.data.message || 'Failed to confirm');
      navigate('/connectors', {
        state: { message: 'Meta re-authentication confirmed (scope reduction acknowledged).', severity: 'warning' }
      });
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to confirm re-authentication.');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 6 }}>
        <CircularProgress size={48} sx={{ mb: 2 }} />
        <Typography>Loading re-authentication details...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4, p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/connectors')} sx={{ mr: 2 }}>
          Back
        </Button>
        <FacebookIcon sx={{ color: '#1877F2', mr: 2, fontSize: 32 }} />
        <Typography variant="h4" sx={{ fontWeight: 600 }}>Confirm Meta Re-authentication</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}

      {warning ? (
        <Card>
          <CardContent>
            <Alert severity="error" icon={<WarningIcon />} sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Scope reduction detected</Typography>
            </Alert>
            <Typography variant="body1" sx={{ mb: 2 }}>
              This Meta account (<strong>{warning.user_name || warning.user_id}</strong>) re-authenticated
              with <strong>{warning.total_lost_pages} fewer page(s)</strong> than before. Because Meta tracks
              page access at the account level, confirming this will revoke those page tokens and break{' '}
              <strong>{warning.affected_connectors.length} existing connector(s)</strong>:
            </Typography>
            <Box sx={{ pl: 1, mb: 3 }}>
              {warning.affected_connectors.map(c => (
                <Box key={c.connector_id} sx={{ mb: 1.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{c.connector_name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Pages that will lose access: {c.lost_pages.map(p => p.page_name || p.page_id).join(', ')}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              <strong>Recommended:</strong> Cancel, then click "Re-authenticate" again and in Meta's page
              selector make sure ALL the pages listed above stay selected (you can add new ones too).
            </Alert>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate('/connectors')}
                disabled={submitting}
              >
                Cancel & re-authenticate properly
              </Button>
              <Button
                variant="outlined"
                color="warning"
                onClick={handleProceed}
                disabled={submitting}
                startIcon={submitting ? <CircularProgress size={18} /> : null}
              >
                {submitting ? 'Confirming...' : 'Proceed anyway (lose those pages)'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      ) : (
        !error && (
          <Alert severity="info">No scope reduction to confirm. <Button onClick={() => navigate('/connectors')}>Back to connectors</Button></Alert>
        )
      )}
    </Box>
  );
};

export default FacebookReauthConfirm;
